import * as Sentry from '@sentry/react'
import dayjs from 'dayjs'

import {
  ApplicationStatus,
  BulkUpdateRoomsFormEntity,
  BulkUpdateRoomsRequest,
  DiffRoomsResponse,
  RoomEntity,
  RoomResponse,
  RoomsResponse,
  RoomTypeEntity,
  WingEntity,
  WriteRoomFormEntity,
} from 'src/types'

type ToEntitiesInput = {
  projectId: string
  cachedResponse: RoomsResponse
  diffResponse: DiffRoomsResponse | undefined
}

type ToRequestInput = {
  formEntity: BulkUpdateRoomsFormEntity
  beforeRooms: RoomEntity[]
}

type ToEntityFromFormEntityInput = {
  formEntity: WriteRoomFormEntity
  wings: WingEntity[]
  roomTypes: RoomTypeEntity[]
}

export const RoomTranslator = {
  toEntity: (input: { projectId: string; response: RoomResponse }): RoomEntity => {
    const { projectId, response } = input
    return {
      id: `${response.id}`,
      projectId,
      name: response.name,
      wing: {
        id: `${response.wingId}`,
        name: response.wingName,
      },
      floor: response.floor ?? '',
      roomType:
        response.roomTypeId && response.roomTypeName
          ? {
              id: `${response.roomTypeId}`,
              name: response.roomTypeName,
            }
          : undefined,
      priority: response.priority,
      isSelectEnabled: response.isSelectEnabled,
      memo: response.memo ?? undefined,
      select: {
        selectedItemIds: response.selectMasterIds
          ? response.selectMasterIds.map(id => `${id}`)
          : [],
        registerLimitAt: response.registrationDueDatetime
          ? new Date(response.registrationDueDatetime)
          : undefined,
      },
      contractor: response.user
        ? {
            id: response.user.id,
            name: response.user.name,
            email: response.user.email,
            isConfirmed: response.user.identification.status === 'approved',
            selectEmailSentTime: response.user.selectEmailSentTimes,
            agreementRequestEmailSentTime: response.user.agreementRequestEmailSentTimes,
            lastSelectEmailSentAt: response.user.lastSelectEmailSentAt
              ? new Date(response.user.lastSelectEmailSentAt)
              : undefined,
            lastAgreementRequestEmailSentAt: response.user.lastAgreementRequestEmailSentAt
              ? new Date(response.user.lastAgreementRequestEmailSentAt)
              : undefined,
          }
        : undefined,
      registrationStatus:
        response.registrationStatus === 'waiting'
          ? 'waiting'
          : response.registrationStatus === 'registered'
          ? 'registered'
          : 'agreed',
      adminMemo: response.adminMemo ?? undefined,
      publishApplicationRequestStatus: response.publishApplicationStatus as ApplicationStatus,
      orderApplicationRequestStatus: response.orderApplicationStatus as ApplicationStatus,
      orderApplicationCanceledAt: response.orderApplicationCanceledAt
        ? new Date(response.orderApplicationCanceledAt)
        : undefined,
      orderApplicationCancelComment: response.orderApplicationCancelComment ?? undefined,
      confirmationCode: response.confirmationCode ?? undefined,

      isLocked: response.isLocked,
      isEnableProxySelectRegistration: response.isEnableProxySelectRegistration,
      isEnableOrderApplication: response.isEnableOrderApplication,
      isEnableRoomUserRegistration: response.isEnableRoomUserRegistration,
      isEnableRoomUserDestroy: response.isEnableRoomUserDestroy,
      isEnableUpdateConfirmationCode: response.isEnableUpdateConfirmationCode,
      isEnableSelectEmailInvitation: response.isEnableSelectEmailInvitation,
      isEnablePublishApplication: response.isEnablePublishApplication,
      isEnableChangeToUnselectable: response.isEnableChangeToUnselectable,
      isEnableSelectEmailAgreementRequest: response.isEnableSelectEmailAgreementRequest,
      isEnableCancelSelectAgreement: response.isEnableCancelSelectAgreement,
      isEnableCancelPublishApplication: response.isEnableCancelPublishApplication,
      isEnableCancelOrderApplication: response.isEnableCancelOrderApplication,
      isEnableExcludeRoomsFromPublishApplication:
        response.isEnableExcludeRoomsFromPublishApplication,
      isEnableExcludeRoomsFromOrderApplication: response.isEnableExcludeRoomsFromOrderApplication,
    }
  },

  /**
   * キャッシュされた住戸情報と差分情報を元にEntityを生成
   * もし差分情報がない場合はキャッシュされた住戸情報のみを元にEntityを生成することで高速にEntityを生成する
   * @param cachedResponse・・キャッシュされた住戸情報、高速に値を返すが最新ではない可能性あり
   * @param diffResponse・・差分情報、キャッシュからの変更内容のみを含んでいる
   * */
  toEntities: ({ projectId, cachedResponse, diffResponse }: ToEntitiesInput): RoomEntity[] => {
    // キャッシュされた住戸情報を元にEntityを生成
    const cachedEntities: Array<RoomEntity> = cachedResponse.rooms.map(room => {
      return RoomTranslator.toEntity({ projectId, response: room })
    })

    // 差分情報がない場合はキャッシュ情報を返す
    if (!diffResponse) {
      return cachedEntities
    }

    // 差分情報がある場合は キャッシュ+差分 でEntityを生成
    return diffResponse.rooms.reduce((accumulator, diffRoom) => {
      if (diffRoom.type === 'created') {
        const createdEntity = RoomTranslator.toEntity({ projectId, response: diffRoom.room })
        return [...accumulator, createdEntity]
      }
      if (diffRoom.type === 'updated') {
        const updatedEntity = RoomTranslator.toEntity({ projectId, response: diffRoom.room })
        return accumulator.map(entity => {
          if (entity.id === updatedEntity.id) {
            return updatedEntity
          }
          return entity
        })
      }
      if (diffRoom.type === 'deleted') {
        return accumulator.filter(entity => entity.id !== `${diffRoom.room.id}`)
      }
      return accumulator
    }, cachedEntities)
  },

  toFormEntity: (entity: RoomEntity): WriteRoomFormEntity => {
    const {
      id: entityId,
      name,
      floor,
      wing,
      roomType,
      memo,
      priority,
      isSelectEnabled,
      select,
      contractor,
      registrationStatus,
      publishApplicationRequestStatus,
      orderApplicationRequestStatus,
      isLocked,
      isEnableProxySelectRegistration,
    } = entity

    const wingId = Number.parseInt(wing.id)
    const roomTypeId = roomType?.id ? Number.parseInt(roomType.id) : undefined

    return {
      id: entityId,
      entityId,
      name,
      floor,
      priority,
      wingId,
      roomTypeId,
      isSelectEnabled,
      memo,
      registrationDueDate: select.registerLimitAt,
      isDeleted: false,
      selectMasters: select.selectedItemIds.map(selectedId => {
        const id = Number.parseInt(selectedId)
        return {
          id,
          selectOptionMasterIds: [],
          isDeleted: false,
          // entityから渡された場合は1度保存されたことがあると判断する
          hasBeenSaved: true,

          // ソート時に利用する値
          idForSorting: id,
        }
      }),

      // ソート時に使用する値
      nameForSorting: name,
      floorForSorting: floor,
      wingIdForSorting: wingId,
      roomTypeIdForSorting: roomTypeId,
      isSelectEnabledForSorting: isSelectEnabled,
      memoForSorting: memo,
      registrationDueDateForSorting: select.registerLimitAt,

      contractor: contractor
        ? {
            id: contractor.id,
            name: contractor.name,
            email: contractor.email,
            isConfirmed: contractor.isConfirmed,
            selectEmailSentTime: contractor.selectEmailSentTime,
            agreementRequestEmailSentTime: contractor.agreementRequestEmailSentTime,
          }
        : undefined,
      registrationStatus,
      publishApplicationRequestStatus,
      orderApplicationRequestStatus,

      isLocked,
      isEnableProxySelectRegistration,

      // 最初はフィルタリングされていない状態であるため基本true
      isFiltered: true,
    }
  },

  toRequest: ({ formEntity, beforeRooms }: ToRequestInput): BulkUpdateRoomsRequest => {
    const filteredItems = formEntity.items.filter(item => {
      // entityIdがなくisDeletedがtrueの場合は、編集モードに移行後新たに作成しようとしたが保存前に削除したものになるため、APIに送信する必要がない
      return !(!item.entityId && !!item.isDeleted)
    })

    const filteredItemsWithWingId = filteredItems.filter(item => {
      if (item.wingId === undefined) {
        // 設定されていない場合はUI上での制御が足りていない
        // sentryに通知しつつ、設定されていないものはfilterで除外
        Sentry.captureMessage(
          `【id:${item.entityId} name: ${item.name}】にwingIdが設定されているべき箇所で設定されていません`
        )
      }
      if (item.roomTypeId === undefined) {
        // 設定されていない場合はUI上での制御が足りていない
        // sentryに通知しつつ、設定されていないものはfilterで除外
        Sentry.captureMessage(
          `【id:${item.roomTypeId} name: ${item.name}】にroomTypeIdが設定されているべき箇所で設定されていません`
        )
      }
      return item.wingId !== undefined && item.roomTypeId !== undefined
    })

    return {
      rooms: filteredItemsWithWingId.map(item => {
        const beforeRoom = beforeRooms.find(room => room.id === item.entityId)

        return {
          id: item.entityId ? Number.parseInt(item.entityId) : undefined,
          name: item.name,
          floor: item.floor,
          priority: item.priority,
          // 少し上のfilterでwingIdがないものは除外しているため強制的に型アサーションで対応
          wingId: item.wingId as number,
          roomTypeId: item.roomTypeId as number,
          isSelectEnabled: item.isSelectEnabled,
          memo: item.memo,
          registrationDueDatetime: item.registrationDueDate
            ? dayjs(item.registrationDueDate).format('YYYY-MM-DD')
            : undefined,
          isDelete: item.isDeleted ?? undefined,
          selectMasters: item.selectMasters
            // NOTE: 住戸xセレクト設問 の紐付けはいくつかの条件において送信しない（送信するとエラーになるため注意）
            .filter(selectMaster => {
              if (selectMaster.isDeleted) {
                // 削除（紐付け解除）しようとしている場合、既に紐付けを行っていた場合のみに絞る
                // 既に紐付けを行っていた場合以外に削除しようとするとエラーになる
                return !!beforeRoom?.select.selectedItemIds.find(id => `${selectMaster.id}` === id)
              }

              // 新たに紐付けを行おうとして回答が1つも選択されていない。1つも選択されていないのは以下2パターン
              // - APIから受け取った回答の情報を変更していない（=以前の選択をした状態のまま）
              // - 回答を選択する際に1つも選択しなかった（UI的に弾いているはずだが制御がうまくいっていなかった場合）
              return selectMaster.selectOptionMasterIds.length > 0
            })
            .map(selectMaster => {
              return {
                id: selectMaster.id,
                selectOptionMasterIds: selectMaster.selectOptionMasterIds,
                isDelete: selectMaster.isDeleted ?? undefined,
              }
            }),
        }
      }),
    }
  },

  toEntityFromFormEntity: (input: ToEntityFromFormEntityInput): Partial<RoomEntity> => {
    const { formEntity, wings, roomTypes } = input

    const wing = formEntity.wingId ? wings.find(w => w.id === `${formEntity.wingId}`) : undefined
    const roomType = formEntity.roomTypeId
      ? roomTypes.find(rt => rt.id === `${formEntity.roomTypeId}`)
      : undefined

    return {
      id: formEntity.id,
      projectId: '',
      name: formEntity.name,
      wing: wing
        ? {
            id: `${wing.id}`,
            name: wing.name,
          }
        : undefined,
      floor: formEntity.floor ?? undefined,
      roomType: roomType
        ? {
            id: `${roomType.id}`,
            name: roomType.name,
          }
        : undefined,
      priority: formEntity.priority,
      isSelectEnabled: formEntity.isSelectEnabled,
      memo: formEntity.memo ?? undefined,
      select: {
        selectedItemIds: formEntity.selectMasters.map(selectMaster => `${selectMaster.id}`),
        registerLimitAt: formEntity.registrationDueDate
          ? new Date(formEntity.registrationDueDate)
          : undefined,
      },
      contractor: formEntity.contractor
        ? {
            id: formEntity.contractor.id,
            name: formEntity.contractor.name,
            email: formEntity.contractor.email,
            isConfirmed: formEntity.contractor.isConfirmed,
            selectEmailSentTime: formEntity.contractor.selectEmailSentTime,
            agreementRequestEmailSentTime: formEntity.contractor.agreementRequestEmailSentTime,
            lastSelectEmailSentAt: undefined,
            lastAgreementRequestEmailSentAt: undefined,
          }
        : undefined,
      registrationStatus: formEntity.registrationStatus,
      adminMemo: undefined,
      publishApplicationRequestStatus: formEntity.publishApplicationRequestStatus ?? undefined,
      orderApplicationRequestStatus: formEntity.orderApplicationRequestStatus ?? undefined,
      isEnableProxySelectRegistration: formEntity.isEnableProxySelectRegistration,
      isEnableOrderApplication: false,
    }
  },
} as const
